import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPrint, FaShare, FaWifi, FaCoffee, FaPlane, FaHome } from "react-icons/fa";
import ContentLayout from "../../../layout/ContentLayout";
import { H2, H3, Paragraph } from "../../../styles/AppStyles";
import { LINKS, href } from "../../../config/links";

const CalloutBox = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  border-left: 5px solid ${({ $type, theme }) => {
    if ($type === "do") return "#10b981";
    if ($type === "dont") return "#ef4444";
    if ($type === "warning") return "#f59e0b";
    if ($type === "tip") return "#6366f1";
    return theme.iconColor;
  }};
  background-color: ${({ $type, theme }) => {
    if ($type === "do") return "rgba(16, 185, 129, 0.08)";
    if ($type === "dont") return "rgba(239, 68, 68, 0.08)";
    if ($type === "warning") return "rgba(245, 158, 11, 0.08)";
    if ($type === "tip") return "rgba(99, 102, 241, 0.08)";
    return "rgba(99, 102, 241, 0.08)";
  }};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  strong {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ $type }) => {
      if ($type === "do") return "#10b981";
      if ($type === "dont") return "#ef4444";
      if ($type === "warning") return "#f59e0b";
      if ($type === "tip") return "#6366f1";
      return "#6366f1";
    }};
  }

  ul {
    margin: 0.75rem 0 0 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

const ChecklistSection = styled.div`
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.iconColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.text};

  h3 {
    margin-top: 0;
  }
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  input[type="checkbox"] {
    margin-top: 0.35rem;
    flex-shrink: 0;
    width: 1.1rem;
    height: 1.1rem;
    cursor: pointer;
  }

  label {
    flex: 1;
    cursor: pointer;
    line-height: 1.6;
  }
`;

const ScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
  margin: 1.5rem 0;
`;

const ScenarioCard = styled.div`
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};

  h4 {
    margin-top: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${({ theme }) => theme.iconColor};
  }

  ul {
    margin: 0.5rem 0 0 0;
    padding-left: 1.25rem;

    li {
      margin-bottom: 0.4rem;
      line-height: 1.5;
      font-size: 0.95rem;
    }
  }
`;

const MistakeTable = styled.table`
  width: 100%;
  margin: 1.5rem 0;
  border-collapse: collapse;
  color: ${({ theme }) => theme.text};

  th {
    background-color: ${({ theme }) => theme.iconColor};
    color: ${({ theme }) => theme.background};
    padding: 0.75rem;
    text-align: left;
    font-weight: 600;
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.iconColor};
    vertical-align: top;
  }

  tr:nth-child(even) {
    background-color: ${({ theme }) =>
      theme.isdarkmode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
  }

  .mistake-text {
    font-weight: 600;
    color: #ef4444;
  }

  .fix-text {
    font-weight: 600;
    color: #10b981;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;

    button,
    a {
      width: 100%;
    }
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background-color: ${({ theme }) => theme.iconColor};
  color: ${({ theme }) => theme.background};
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const RemoteWorkSecurityGuide = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.REMOTE_WORK_SECURITY);

  // const heroImageSrc = "/images/guides/remote-work-security.webp";

  return (
    <ContentLayout
        istouch={istouch}
        isdarkmode={isdarkmode}
        displayTitle="Secure Your Home Office & Remote Work"
        seoTitle="Remote Work Security Guide: Home Office & Travel | CSFSafe"
        canonicalUrl={canonicalUrl}
        description="Practical remote work security: home WiFi, VPN usage, device hardening, secure video calls, and safe file sharing for distributed teams."
      ogTitle="Remote Work Security Guide: Home Office & Travel | CSFSafe"
      ogDescription="Secure your home WiFi, VPN, devices, and file sharing wherever you work. Setup checklist for SMB remote employees."
      // ogImage={heroImageSrc}
      // heroImageSrc={heroImageSrc}
      heroImageAlt="Remote work security and home office setup"
      publishedLabel="Quick Reference Guide — July 22, 2026"
      showCTA={true}
    >
      <Paragraph>
        Remote and hybrid work expanded the boundaries of the corporate
        network to home offices, coffee shops, and airport lounges. Each of
        those locations carries different risks. This guide covers the
        essentials—WiFi security, VPN usage, device hardening, secure
        conferencing, physical security, and safe file sharing—wherever you
        work.
      </Paragraph>

      <H2>Home WiFi Security</H2>

      <H3>Lock Down the Router</H3>

      <CalloutBox $type="do">
        <strong>Secure Your Router</strong>
        <ul>
          <li>
            Change the default router admin password—many are shipped with
            widely known defaults
          </li>
          <li>
            Use WPA3 (or WPA2 at minimum) encryption; never leave WiFi open
          </li>
          <li>
            Set a strong, unique WiFi password—not your address or phone
            number
          </li>
          <li>
            Rename your network (SSID) so it doesn't reveal the router model
            or your name
          </li>
          <li>
            Keep router firmware updated—check the manufacturer's app or admin
            page quarterly
          </li>
          <li>
            Set up a separate guest network for visitors, smart home devices,
            and personal devices, keeping work devices isolated
          </li>
        </ul>
      </CalloutBox>

      <H2>VPN Usage</H2>

      <Paragraph>
        A Virtual Private Network (VPN) encrypts traffic between your device
        and your company's network or the internet, protecting data from
        being intercepted—especially important on shared or public networks.
      </Paragraph>

      <H3>Using Your Company VPN</H3>

      <CalloutBox $type="tip">
        <strong>VPN Best Practices</strong>
        <ul>
          <li>
            Always connect to your company VPN before accessing internal
            systems, email, or shared drives remotely
          </li>
          <li>
            Never disable the VPN to "speed things up"—if it's slow, report it
            to IT rather than bypassing it
          </li>
          <li>
            Use company-approved VPN software only—avoid free consumer VPNs
            for work use, as some log or sell your data
          </li>
          <li>
            Enable "always-on" VPN settings if your organization supports it,
            so connections are protected automatically
          </li>
          <li>
            If your VPN disconnects, stop working on sensitive data until it
            reconnects
          </li>
        </ul>
      </CalloutBox>

      <H2>Device Hardening</H2>

      <ChecklistSection>
        <h3>Remote Work Device Checklist</h3>
        <ChecklistItem>
          <input type="checkbox" id="item1" />
          <label htmlFor="item1">
            Full-disk encryption enabled (BitLocker on Windows, FileVault on
            Mac)
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item2" />
          <label htmlFor="item2">
            Screen lock set to activate after 5 minutes or less of inactivity
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item3" />
          <label htmlFor="item3">
            OS and applications set to auto-update or updated weekly
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item4" />
          <label htmlFor="item4">
            Antivirus/endpoint protection installed and active
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item5" />
          <label htmlFor="item5">
            Firewall enabled on the device itself, not just the router
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item6" />
          <label htmlFor="item6">
            No personal apps or browser extensions installed on the work
            device without IT approval
          </label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item7" />
          <label htmlFor="item7">
            Multi-factor authentication enabled on all work accounts
          </label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Secure Video Conferencing</H2>

      <CalloutBox $type="tip">
        <strong>Protect Your Meetings</strong>
        <ul>
          <li>Use a waiting room or lobby feature so hosts approve entry</li>
          <li>Require a meeting password for sensitive discussions</li>
          <li>Don't reuse the same meeting link/room for every call</li>
          <li>Be aware of what's visible behind you—use a blur or virtual background for sensitive environments</li>
          <li>Mute notifications and lock your screen when stepping away mid-meeting</li>
          <li>Only share your screen after closing unrelated sensitive documents or tabs</li>
        </ul>
      </CalloutBox>

      <H2>Physical Security at Home & On the Go</H2>

      <CalloutBox $type="warning">
        <strong>Don't Overlook Physical Risks</strong>
        <ul>
          <li>Lock your device screen every time you step away, even at home</li>
          <li>Store laptops out of sight in vehicles—never leave them visible in a parked car</li>
          <li>Use a privacy screen filter in public spaces to prevent shoulder surfing</li>
          <li>Keep work devices separate from household members' devices and accounts</li>
          <li>Shred or securely dispose of printed sensitive documents at home</li>
          <li>Report a lost or stolen device to IT immediately—delays widen the exposure window</li>
        </ul>
      </CalloutBox>

      <H2>Safe File Sharing</H2>

      <CalloutBox $type="do">
        <strong>DO Share Files This Way</strong>
        <ul>
          <li>Use company-approved cloud storage (SharePoint, OneDrive, Google Workspace, etc.) with access controls</li>
          <li>Set expiration dates and permissions on shared links when possible</li>
          <li>Verify the recipient's email address before sharing sensitive files</li>
          <li>Use encrypted file transfer for highly sensitive data</li>
        </ul>
      </CalloutBox>

      <CalloutBox $type="dont">
        <strong>DON'T Do These Things</strong>
        <ul>
          <li>Don't email sensitive files as unencrypted attachments</li>
          <li>Don't use personal cloud storage (personal Dropbox, Gmail, etc.) for work files</li>
          <li>Don't use USB drives to move sensitive data between devices without encryption</li>
          <li>Don't leave shared links set to "anyone with the link" for confidential material</li>
        </ul>
      </CalloutBox>

      <H2>Setup Checklist: Get This Right Before You Start Working</H2>

      <ChecklistSection>
        <h3>Home Office Setup</h3>
        <ChecklistItem>
          <input type="checkbox" id="item8" />
          <label htmlFor="item8">Router admin password changed from default</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item9" />
          <label htmlFor="item9">WiFi using WPA3/WPA2 with a strong password</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item10" />
          <label htmlFor="item10">Company VPN installed, tested, and set to auto-connect</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item11" />
          <label htmlFor="item11">Device encryption, screen lock, and antivirus confirmed active</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item12" />
          <label htmlFor="item12">MFA enabled on email, VPN, and cloud storage accounts</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item13" />
          <label htmlFor="item13">Workspace positioned so screens aren't visible from windows or shared spaces</label>
        </ChecklistItem>
        <ChecklistItem>
          <input type="checkbox" id="item14" />
          <label htmlFor="item14">Know who to contact and how if a device is lost, stolen, or compromised</label>
        </ChecklistItem>
      </ChecklistSection>

      <H2>Device Location Scenarios</H2>

      <ScenarioGrid>
        <ScenarioCard>
          <h4>
            <FaHome /> At Home
          </h4>
          <ul>
            <li>Use your secured home network, not a neighbor's open WiFi</li>
            <li>Keep smart home/IoT devices on a separate guest network</li>
            <li>Lock your screen when family members are nearby</li>
            <li>Store devices securely when not in use</li>
          </ul>
        </ScenarioCard>
        <ScenarioCard>
          <h4>
            <FaCoffee /> Coffee Shop / Public WiFi
          </h4>
          <ul>
            <li>Always connect to VPN before doing any work</li>
            <li>Verify the network name with staff—fake lookalike hotspots are common</li>
            <li>Use a privacy screen and sit facing away from open sightlines</li>
            <li>Never leave your device unattended, even briefly</li>
          </ul>
        </ScenarioCard>
        <ScenarioCard>
          <h4>
            <FaPlane /> Airport / Travel
          </h4>
          <ul>
            <li>Avoid public charging stations for data cables—use your own charger/outlet</li>
            <li>Keep devices in carry-on luggage, never checked bags</li>
            <li>Use cellular hotspot instead of public WiFi when handling sensitive work</li>
            <li>Enable device tracking/remote wipe before you travel</li>
          </ul>
        </ScenarioCard>
        <ScenarioCard>
          <h4>
            <FaWifi /> Client Site / Shared Office
          </h4>
          <ul>
            <li>Confirm you're joining the intended network, not a similarly named guest one</li>
            <li>Don't leave printed materials on shared desks</li>
            <li>Log out of accounts on any shared or borrowed computer</li>
            <li>Use your VPN even if the site's network feels "trusted"</li>
          </ul>
        </ScenarioCard>
      </ScenarioGrid>

      <H2>Common Mistakes and Fixes</H2>

      <MistakeTable>
        <thead>
          <tr>
            <th>Common Mistake</th>
            <th>Fix</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="mistake-text">Using the router's default admin password</td>
            <td className="fix-text">Change it immediately and store the new one in a password manager</td>
          </tr>
          <tr>
            <td className="mistake-text">Skipping the VPN because it's "slow" or "annoying"</td>
            <td className="fix-text">Report performance issues to IT rather than working unprotected</td>
          </tr>
          <tr>
            <td className="mistake-text">Saving work files to a personal cloud account</td>
            <td className="fix-text">Use only company-approved, access-controlled storage</td>
          </tr>
          <tr>
            <td className="mistake-text">Letting family members use a work laptop</td>
            <td className="fix-text">Keep work and personal devices completely separate</td>
          </tr>
          <tr>
            <td className="mistake-text">Leaving a laptop unlocked "just for a minute"</td>
            <td className="fix-text">Lock the screen every single time you step away</td>
          </tr>
          <tr>
            <td className="mistake-text">Joining video calls from public WiFi without a VPN</td>
            <td className="fix-text">Connect VPN first, or use a personal hotspot for sensitive calls</td>
          </tr>
        </tbody>
      </MistakeTable>

      <H2>Related Resources</H2>
      <ul>
        <li>
          <a href={href(LINKS.THREAT_TYPE.REMOTE_ACCESS_ABUSE)}>
            Remote Access Abuse Threat Type
          </a>
        </li>
        <li>
          <a href={href(LINKS.BEST_PRACTICES.NETWORK_INFRASTRUCTURE_SECURITY)}>
            Network Infrastructure Security
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.SECURE_WINDOWS_LAPTOP)}>
            Securing Your Windows 11 Computer
          </a>
        </li>
        <li>
          <a href={href(LINKS.ARTICLE.SECURE_MACBOOK)}>
            Securing Your MacBook
          </a>
        </li>
      </ul>

      <ButtonRow data-print="hide">
        <Button
          as="button"
          type="button"
          onClick={() => window.print()}
          style={{ fontFamily: "inherit" }}
        >
          <FaPrint /> Print or save as PDF
        </Button>
        <Button
          href="https://www.facebook.com/sharer/sharer.php?u=https://csfsafe.com/resources/quick-guides/remote-work-security"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaShare /> Share Guide
        </Button>
      </ButtonRow>
      </ContentLayout>
  );
};

RemoteWorkSecurityGuide.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default RemoteWorkSecurityGuide;
